export type IndustryDef = {
  slug: string;
  title: string;
  short: string;
  seoTitle: string;
  seoDescription: string;
  hero: {
    headline: string;
    subheadline: string;
    primaryCta: string;
    secondaryCta: string;
  };
  problem: {
    intro?: string;
    bullets: string[];
    closing?: string;
  };
  whyErpNotEnough: string;
  howBeedigitHelps: string[];
  modules: string[];
  kpis: string[];
  dashboardWidgets: string[];
  leanAngle: string;
  leanMessage: string;
  startingPoint: string;
  finalCta: string;
  // legacy — kept so other components compiling against the old shape still work
  text: string;
  features: string[];
};

export const industries: IndustryDef[] = [
  {
    slug: "cable-wire-manufacturing",
    title: "Cable & Wire Manufacturing",
    short:
      "Control line speed, output meters, drums, material consumption, downtime, quality and energy per meter. Validate real production speed against planning, costing and APS standards.",
    seoTitle:
      "BEEDIGIT for Cable & Wire Manufacturing | Line Speed, OEE, Drum QR & Energy per Meter",
    seoDescription:
      "BEEDIGIT helps cable and wire manufacturers control real line speed, output meters, OEE, downtime, drum QR traceability, material consumption, quality and energy per meter using trusted real-time shop-floor data.",
    hero: {
      headline: "Control cable line speed, output, quality and energy in real time.",
      subheadline:
        "In cable and wire production, line speed is not only a production number. It directly affects output, quality, costing, capacity planning and delivery performance. BEEDIGIT captures actual line speed, output meters, downtime, energy, material consumption, quality checks and drum traceability so cable factories can manufacture based on real data, not assumptions.",
      primaryCta: "Book Cable Line Visibility Assessment",
      secondaryCta: "Start with One Cable Line",
    },
    problem: {
      intro:
        "Cable factories often plan production using standard speeds stored in ERP, APS, Excel or routing master data. But the real production speed is usually different — it changes by product family, conductor size, insulation material, cable construction, color, compound quality, tooling, drum size, operator, machine condition, customer specification and quality requirements.",
      bullets: [
        "If the actual line runs slower than the standard, the factory loses output and delivery promises become unrealistic.",
        "If the line runs faster than the stable process window, insulation defects, eccentricity issues, spark test failures and rework increase.",
        "Speed standards influence cost calculation, capacity booking, quotation accuracy and OEE.",
        "Outdated or manually adjusted standards distort planning and hide the real Lean improvement priorities.",
        "Processes involved: rod breakdown, wire drawing, bunching, stranding, insulation, extrusion, screening, armouring, cabling, sheathing, rewinding, testing, drum handling and packing.",
      ],
      closing:
        "BEEDIGIT helps cable manufacturers replace theoretical line speeds with trusted real production speed data.",
    },
    whyErpNotEnough:
      "ERP plans the business and APS plans capacity, but neither of them measures what really happens on the drawing, extrusion or cabling machine. Without automatic line data, speed, downtime and energy losses stay invisible until the end of the shift.",
    howBeedigitHelps: [
      "Track actual line speed by machine, order, product, drum, shift and operator.",
      "Compare actual speed vs standard speed used in ERP, APS, costing and planning.",
      "Detect slow running and classify the reason.",
      "Detect risky overspeed when speed exceeds the quality-safe process window.",
      "Track output in meters, drums, batches and finished goods.",
      "Track OEE by line, product family, cable type, order and shift.",
      "Classify downtime — setup, drum change, material waiting, compound issue, tooling, maintenance, quality hold, operator issue, planned stop.",
      "Detect micro-stops that are usually invisible in manual reports.",
      "Track actual material consumption vs BOM norm (copper, aluminum, compound, insulation, packaging).",
      "Track energy consumption per meter, drum, batch, order and product family.",
      "Print QR labels for raw materials, semi-finished goods and finished drums.",
      "Build genealogy from raw material batch to final cable drum.",
      "Give operators digital job orders, instructions, quality checks and confirmations on industrial tablets.",
      "Feed actual production data back to ERP, APS, BI or factory.online.",
    ],
    modules: [
      "Machine Monitoring",
      "Line Speed Tracking",
      "OEE & Downtime",
      "MES / Job Orders",
      "Material Flow Control",
      "QR Coding & Drum Traceability",
      "Quality Management",
      "Energy Management",
      "CMMS / Maintenance",
      "ANDON",
      "ERP / APS Integration",
    ],
    kpis: [
      "Actual line speed vs standard",
      "Speed loss by product / order / machine / shift",
      "Output meters per shift",
      "Output vs target",
      "OEE by cable line",
      "Availability",
      "Performance",
      "Quality rate",
      "Downtime by reason",
      "Micro-stops",
      "Setup time",
      "Drum change time",
      "Scrap meters",
      "Rework meters",
      "Energy per meter",
      "Energy per drum",
      "Energy per order",
      "Material vs BOM norm",
      "Copper / aluminum / compound overconsumption",
      "Spark test failure rate",
      "Finished drum genealogy",
      "Plan vs actual production time",
      "Delivery delay caused by production losses",
      "Actual cost vs standard cost",
    ],
    dashboardWidgets: [
      "Line speed",
      "Output meters",
      "Drum status",
      "Energy per meter",
      "Downtime by reason",
      "Speed vs standard",
      "Quality alerts",
      "Material consumption vs BOM",
    ],
    leanAngle:
      "Cable production improvement depends on trusted line-level data. BEEDIGIT gives Lean, TPM and Six Sigma teams real Gemba data for speed loss Pareto, downtime Pareto, setup reduction, drum change improvement, energy loss analysis, quality root cause analysis, material overconsumption analysis, standard speed validation and DMAIC projects.",
    leanMessage: "Make every meter, minute and kilowatt visible.",
    startingPoint:
      "Start with one high-volume cable line or one bottleneck process where speed, downtime or energy losses have the biggest business impact.",
    finalCta:
      "Find the real speed, downtime and energy losses hidden in your cable production.",
    text: "Trusted real-time visibility for cable and wire manufacturing.",
    features: [
      "Actual vs standard line speed",
      "Drum QR traceability",
      "Energy per meter",
      "OEE by cable line",
      "Material consumption vs BOM",
      "ERP / APS feedback",
    ],
  },
  {
    slug: "fiber-optic-cable-manufacturing",
    title: "Fiber Optic Cable Manufacturing",
    short:
      "Monitor every fiber batch from preform to finished drum — line speed, draw tension, coating geometry, proof-test, attenuation and OTDR — with full batch genealogy and quality hold/release control.",
    seoTitle:
      "BEEDIGIT for Fiber Optic Cable Manufacturing | Batch Traceability, Proof-Test, OTDR & OEE",
    seoDescription:
      "BEEDIGIT helps fiber optic cable manufacturers monitor each batch from preform to drum — line speed, draw tension, coating diameter, proof-test, attenuation, OTDR, quality holds and full batch genealogy in real time.",
    hero: {
      headline: "Monitor every fiber batch from preform to finished drum.",
      subheadline:
        "Fiber optic production is precision- and quality-critical. Every kilometer of fiber is high-value, and each batch must carry its identity from preform through drawing, coating, proof testing, coloring, ribbon, cabling and final drum. BEEDIGIT captures real-time line speed, draw tension, coating geometry, cure temperature, proof-test, attenuation and OTDR per batch — so quality, traceability and batch monitoring become trusted shop-floor data instead of end-of-shift paperwork.",
      primaryCta: "Book Fiber Line Visibility Assessment",
      secondaryCta: "Start with One Draw Tower",
    },
    problem: {
      intro:
        "Fiber optic manufacturing depends on tight process windows and complete batch identity. Attenuation, core/clad/coating concentricity, tensile strength and proof-test level must all stay inside specification for every kilometer produced.",
      bullets: [
        "Draw-tower breaks, coating cure issues and tension instability create scrap and lost fiber kilometers that are expensive and hard to recover.",
        "Standard draw speed and coating parameters are often theoretical; the real stable window shifts with preform quality, ambient conditions and tooling state.",
        "Quality results — geometry, attenuation, tensile, proof-test — arrive at end-of-shift or batch-end, too late to stop a drifting process.",
        "Batch identity is fragmented: preform, fiber spool, ribbon, cable section and finished drum are often tracked in separate records, breaking genealogy and recall readiness.",
        "OTDR and final-test results are stored separately from the batch and machine that produced the fiber, making root-cause analysis slow.",
        "Micro-stops, splice time and setup losses on the draw tower and cabling line are invisible in manual reports.",
        "Energy consumed per kilometer of fiber is not linked to batch, order or product, distorting costing and capacity decisions.",
      ],
      closing:
        "BEEDIGIT helps fiber optic manufacturers replace batch-end paperwork with trusted real-time batch monitoring and full genealogy.",
    },
    whyErpNotEnough:
      "ERP plans the order and the BOM, but it does not know the draw speed, the coating diameter, the proof-test result or which preform became which fiber drum. Without automatic line and batch data, quality risk and traceability gaps stay hidden until the customer audit or claim.",
    howBeedigitHelps: [
      "Track each preform and batch through drawing, coating, proof testing, coloring, ribbon, cabling, sheathing and final testing in real time.",
      "Monitor draw-tower line speed, draw tension, coating diameter and cure temperature per batch and compare against the standard process window.",
      "Detect slow running, micro-stops and draw-tower breaks and classify the reason automatically.",
      "Capture proof-test level and pass/fail per fiber length and link it to batch, machine and operator.",
      "Record geometry (core/clad/coating concentricity, diameter) and attenuation results per batch with digital quality checks.",
      "Run a nonconformance, hold and release workflow so suspect batches are blocked before they reach the next stage.",
      "Build defect Pareto by batch, machine, operator and product family so quality root cause analysis is fast.",
      "Attach OTDR traces and final-test results directly to the batch and finished drum record.",
      "Print QR labels for preform, fiber spool, ribbon, cable section and finished drum.",
      "Build full genealogy from raw preform through fiber kilometers to finished cable drum — recall-ready in minutes.",
      "Track output meters, fiber yield per preform, scrap meters and rework by batch and shift.",
      "Track energy consumption per kilometer, batch, order and product family.",
      "Give operators digital job orders, process instructions, quality checks and confirmations on industrial tablets.",
      "Use Andon for draw-tower breaks, coating cure faults, material issues, quality holds and maintenance needs.",
      "Feed actual production, quality and material data back to ERP, APS, BI or factory.online.",
    ],
    modules: [
      "Machine Monitoring",
      "Line Speed Tracking",
      "OEE & Downtime",
      "MES / Job Orders",
      "Material Flow Control",
      "QR Coding & Batch Traceability",
      "Quality Management",
      "Energy Management",
      "CMMS / Maintenance",
      "ANDON",
      "ERP / APS Integration",
    ],
    kpis: [
      "Actual draw speed vs standard",
      "Draw-tension stability",
      "Coating diameter deviation",
      "Cure temperature vs target",
      "Draw-tower break count",
      "Output meters per shift",
      "Fiber yield per preform",
      "OEE by line",
      "Availability",
      "Performance",
      "Quality rate",
      "Proof-test pass rate",
      "Geometry pass rate",
      "Attenuation by batch",
      "OTDR pass rate",
      "First-pass yield",
      "Scrap meters",
      "Rework meters",
      "Micro-stops",
      "Setup time",
      "Downtime by reason",
      "Batch genealogy completeness",
      "Finished drum traceability",
      "Energy per km",
      "Energy per batch",
      "Energy per order",
      "Material vs BOM norm",
      "Nonconformance hold time",
      "Customer claim rate",
    ],
    dashboardWidgets: [
      "Draw speed",
      "Batch status",
      "Attenuation",
      "Geometry pass rate",
      "Proof-test",
      "OTDR",
      "Coating diameter",
      "Energy per km",
    ],
    leanAngle:
      "Fiber optic production improvement depends on trusted batch-level data. BEEDIGIT gives Lean, TPM and Six Sigma teams real Gemba data for draw-tower break Pareto, coating-defect root cause, proof-test failure analysis, setup reduction, energy loss analysis, batch yield improvement and DMAIC projects — all anchored to batch, machine and operator.",
    leanMessage: "Make every batch, kilometer and proof-test visible.",
    startingPoint:
      "Start with one draw tower or one bottleneck cabling line where breaks, scrap or quality losses have the biggest business impact.",
    finalCta:
      "Find the real breaks, scrap and quality losses hidden in your fiber optic production.",
    text: "Trusted real-time batch monitoring and traceability for fiber optic cable manufacturing.",
    features: [
      "Per-batch draw monitoring",
      "Proof-test and OTDR tracking",
      "Batch genealogy to drum",
      "Geometry and attenuation QC",
      "Energy per km",
      "ERP / APS feedback",
    ],
  },
  {
    slug: "plastics-extrusion",
    title: "Plastics, Extrusion & Continuous Processing",
    short:
      "Track kg/hour, meter/minute, startup scrap, process stability, changeovers, downtime, quality checks and energy per kg, meter or batch.",
    seoTitle:
      "BEEDIGIT for Plastics & Extrusion | Line Speed, Scrap, OEE & Energy per kg",
    seoDescription:
      "BEEDIGIT helps plastics and extrusion manufacturers track actual kg/hour, line speed, OEE, scrap, downtime, changeovers, quality and energy per kg or batch in real time.",
    hero: {
      headline: "Increase extrusion output without losing process stability.",
      subheadline:
        "In extrusion and continuous processing, every small speed loss, unstable setup, material change or process stop becomes lost output, scrap and energy waste. BEEDIGIT gives real-time visibility into line speed, kg/hour, downtime, scrap, quality checks and energy consumption.",
      primaryCta: "Book Industry Visibility Assessment",
      secondaryCta: "Start with One Extrusion Line",
    },
    problem: {
      intro:
        "Extrusion factories balance speed, quality and process stability. Running too slowly reduces output and increases cost. Running too fast can create unstable dimensions, surface defects, weak quality, excessive scrap or customer complaints.",
      bullets: [
        "Actual output depends on product, material, recipe, line speed, screw speed, tool and die condition.",
        "Temperature stability, cooling performance and operator settings all shift the real rate.",
        "Color change, startup scrap and material quality distort theoretical kg/hour standards.",
        "Line stoppages and inspection frequency add losses that manual reports rarely capture.",
      ],
      closing:
        "If this data is not captured automatically, planning becomes inaccurate, costing becomes unreliable and improvement teams cannot see the real losses.",
    },
    whyErpNotEnough:
      "ERP knows the order and the BOM. It does not know the actual kg/hour, the startup scrap, the micro-stops or the energy consumed while the extruder was idle. BEEDIGIT closes that gap with trusted real-time shop-floor data.",
    howBeedigitHelps: [
      "Track actual kg/hour, meter/minute, pieces/hour or batch output.",
      "Compare actual production rate vs planned production rate.",
      "Detect speed loss, slow running and micro-stops.",
      "Track startup scrap, process scrap and quality rejects.",
      "Track changeover time by material, color, product, tool or recipe.",
      "Capture operator downtime reasons and quality issues.",
      "Connect quality checks to product, order, batch, machine and operator.",
      "Track energy per kg, meter, batch, order or product.",
      "Detect idle energy during waiting, setup and stopped-line conditions.",
      "Use QR codes for material batches, WIP, finished goods and pallets.",
      "Feed actual output, time and consumption back to ERP or APS.",
      "Build a reliable baseline for Lean, Kaizen, TPM and Six Sigma projects.",
    ],
    modules: [
      "Machine Monitoring",
      "OEE & Downtime",
      "MES / Job Orders",
      "Material Flow Control",
      "QR Traceability",
      "Quality Management",
      "Energy Management",
      "ANDON",
      "CMMS / Maintenance",
      "ERP / APS Integration",
    ],
    kpis: [
      "Actual kg/hour vs planned",
      "Actual meter/minute vs standard",
      "Output per shift",
      "OEE by extrusion line",
      "Performance loss",
      "Startup scrap",
      "Process scrap",
      "Scrap rate by material / product",
      "Changeover time",
      "Color change time",
      "Tool / die change time",
      "Downtime by reason",
      "Micro-stops",
      "Energy per kg / meter / batch",
      "Quality defects by order and machine",
      "First-pass yield",
      "Plan vs actual production duration",
      "Operator response time",
      "Maintenance-related downtime",
    ],
    dashboardWidgets: [
      "kg/hour",
      "meter/minute",
      "Startup scrap",
      "Energy per kg",
      "Changeover time",
      "Process stops",
      "Quality defects",
    ],
    leanAngle:
      "BEEDIGIT helps extrusion teams reduce speed losses, startup scrap, changeover time, process instability, material waste and energy losses. It supports DMAIC projects with trusted baseline data and helps TPM teams connect downtime with machine condition.",
    leanMessage: "Reduce waste in speed, scrap, setup and energy.",
    startingPoint:
      "Start with one extrusion line where scrap, energy or speed instability is most expensive.",
    finalCta: "See where your extrusion line loses output, material and energy.",
    text: "Real-time visibility for extrusion and continuous processing.",
    features: [
      "Actual kg/hour vs plan",
      "Startup and process scrap",
      "Changeover tracking",
      "Energy per kg / batch",
      "OEE by extrusion line",
      "ERP / APS feedback",
    ],
  },
  {
    slug: "discrete-manufacturing",
    title: "Discrete Manufacturing, Machining & Assembly",
    short:
      "Digitize job orders, monitor work centers, track cycle time, setup time, WIP, quality, scrap, rework and real machine utilization.",
    seoTitle:
      "BEEDIGIT for Discrete Manufacturing | Job Orders, Cycle Time, OEE & WIP",
    seoDescription:
      "BEEDIGIT helps discrete manufacturers digitize job orders, monitor machines, track cycle time, setup time, WIP, quality, rework and real work center performance.",
    hero: {
      headline: "Turn every work center into a visible, measurable operation.",
      subheadline:
        "BEEDIGIT helps machining, fabrication, assembly and job-shop manufacturers control real machine utilization, job execution, setup time, cycle time, WIP, quality checks and operator confirmations.",
      primaryCta: "Book Work Center Visibility Assessment",
      secondaryCta: "Start with One Bottleneck",
    },
    problem: {
      intro:
        "In discrete manufacturing, ERP may show the production order, but it usually does not show the real status of every work center.",
      bullets: [
        "CNC machines are available on paper but idle in reality.",
        "Operators wait for materials, drawings, tools, programs or supervisor decisions.",
        "Setup time is underestimated and cycle time standards are outdated.",
        "Job progress is updated only after the shift; WIP is difficult to locate.",
        "Quality problems are recorded separately from the job.",
        "Maintenance issues are not connected to production losses.",
        "APS scheduling becomes inaccurate because real capacity is not measured.",
      ],
      closing:
        "In high-mix production, wrong cycle time and setup time standards destroy planning, costing and delivery promises.",
    },
    whyErpNotEnough:
      "ERP tracks the order, not the operator, the setup or the reason a machine is idle. Manual shift reports arrive too late to react. BEEDIGIT gives supervisors the missing shop-floor layer in real time.",
    howBeedigitHelps: [
      "Monitor machine status: running, stopped, idle, setup, maintenance.",
      "Track actual cycle time vs standard cycle time.",
      "Track setup time by product, machine, operator and job type.",
      "Digitize job orders and show priorities on BEEPAD industrial tablets.",
      "Provide digital drawings, attachments, work instructions and checklists.",
      "Capture output, scrap, rework and job progress in real time.",
      "Track WIP movement between work centers using QR codes.",
      "Use Andon for missing material, tooling problems, drawing issues, quality holds and maintenance needs.",
      "Connect quality checks to job, order, machine and operator.",
      "Track maintenance requests and machine-hour based maintenance.",
      "Improve routing standards using actual production data.",
      "Send actual job status and confirmations back to ERP or APS.",
    ],
    modules: [
      "MES / Job Orders",
      "Machine Monitoring",
      "OEE & Downtime",
      "Material Flow",
      "QR Coding",
      "Quality Management",
      "CMMS / Maintenance",
      "ANDON",
      "Dashboards & AI Insights",
      "ERP / APS Integration",
    ],
    kpis: [
      "Machine utilization",
      "Spindle utilization (where available)",
      "Actual cycle time vs standard",
      "Setup time",
      "Waiting time by reason",
      "OEE by work center",
      "Output vs target",
      "Scrap rate",
      "Rework rate",
      "First-pass yield",
      "Job order completion status",
      "WIP by work center",
      "Plan vs actual job duration",
      "Late jobs",
      "Maintenance response time",
      "MTBF",
      "MTTR",
      "Operator productivity",
      "Bottleneck utilization",
    ],
    dashboardWidgets: [
      "Job order progress",
      "Cycle time vs standard",
      "Setup time",
      "WIP by work center",
      "Machine utilization",
      "Late jobs",
      "Scrap and rework",
    ],
    leanAngle:
      "BEEDIGIT gives discrete manufacturers real data for bottleneck analysis, setup reduction, standard work, takt vs actual cycle control, WIP reduction, waiting waste reduction, quality defect reduction, TPM and daily management meetings.",
    leanMessage: "Make every job, machine and bottleneck visible.",
    startingPoint:
      "Start with one bottleneck work center, CNC group, assembly cell or high-value production route.",
    finalCta:
      "Find the hidden losses in your machining, assembly or job-shop production.",
    text: "Real-time visibility across every work center.",
    features: [
      "Digital job orders",
      "Machine and work center monitoring",
      "Cycle and setup time tracking",
      "WIP by work center",
      "Quality and rework tracking",
      "ERP / APS confirmations",
    ],
  },
  {
    slug: "switchgear-electrical-panels",
    title: "Switchgear, Electrical Panels & Project-Based Manufacturing",
    short:
      "Control project-based manufacturing with digital job orders, material kits, assembly stages, QR traceability, quality gates, testing checklists and delivery progress.",
    seoTitle:
      "BEEDIGIT for Switchgear & Electrical Panels | Project MES, Material Kits, QR & FAT",
    seoDescription:
      "BEEDIGIT helps switchgear and electrical panel manufacturers control project-based production with digital job orders, material kits, QR traceability, assembly stages, quality gates and FAT checklists.",
    hero: {
      headline: "Bring MES visibility to project-based electrical panel manufacturing.",
      subheadline:
        "Switchgear and electrical panel production is not only about machine monitoring. It is about controlling engineering changes, BOM kits, assembly stages, wiring, inspections, FAT testing and delivery progress. BEEDIGIT gives real-time visibility from material preparation to final test.",
      primaryCta: "Book Project Execution Assessment",
      secondaryCta: "Start with One Assembly Area",
    },
    problem: {
      intro:
        "Switchgear and panel manufacturing is complex because many orders are engineer-to-order or configure-to-order. Each project may have different drawings, BOMs, components, assembly logic and testing requirements.",
      bullets: [
        "Missing components during assembly and incomplete material kits.",
        "BOM revisions, engineering changes and unclear project progress.",
        "Manual work instructions and paper quality checklists.",
        "Delayed FAT documentation and rework from wiring or component mistakes.",
        "Poor visibility between engineering, warehouse, assembly, testing and quality.",
        "Delivery dates depending on many small execution steps.",
        "ERP shows order status but not the real project execution status.",
      ],
      closing:
        "Traditional OEE alone is not enough here. The main problem is project execution control.",
    },
    whyErpNotEnough:
      "ERP records the sales order and the BOM. It does not tell you which panel is waiting for a component, which wiring step is late or whether the FAT checklist is complete. BEEDIGIT adds project execution visibility on top of ERP.",
    howBeedigitHelps: [
      "Create digital job orders by project, panel, section, assembly stage or work center.",
      "Track project progress in real time.",
      "Connect BOM kits and materials to specific panels or subassemblies.",
      "Use QR codes for cabinets, panels, components, kits and finished goods.",
      "Give operators digital drawings, wiring instructions, checklists and attachments.",
      "Track mechanical assembly, wiring, busbar installation, labeling, testing and packing stages.",
      "Digitize quality gates and FAT checklists.",
      "Attach photos, documents and inspection results to the job.",
      "Track missing material, engineering changes, quality holds and rework.",
      "Escalate issues using Andon workflows.",
      "Give management real-time visibility of project status and bottlenecks.",
      "Connect finished confirmations and material consumption to ERP.",
    ],
    modules: [
      "MES / Job Orders",
      "Material Flow Control",
      "QR Coding & Traceability",
      "Quality Management",
      "Digital Checklists",
      "ANDON",
      "Dashboards",
      "ERP Integration",
      "CMMS / Maintenance",
    ],
    kpis: [
      "Project progress by stage",
      "Panel completion status",
      "Assembly lead time",
      "Wiring lead time",
      "Testing lead time",
      "FAT pass / fail rate",
      "Rework hours",
      "Missing material delays",
      "Engineering change impact",
      "Material kit completeness",
      "WIP by project",
      "Operator confirmations",
      "Quality checklist completion",
      "On-time project delivery",
      "Plan vs actual labor hours",
      "Bottleneck stage",
      "Customer order status accuracy",
    ],
    dashboardWidgets: [
      "Project progress",
      "Panel status",
      "Missing components",
      "Assembly stage",
      "Wiring stage",
      "FAT checklist",
      "Rework hours",
    ],
    leanAngle:
      "BEEDIGIT helps switchgear manufacturers improve flow between engineering, warehouse, assembly and testing. It supports standard work, visual management by project, rework reduction, missing material root cause analysis, quality gate discipline and daily project execution meetings.",
    leanMessage: "Make project manufacturing visible from BOM kit to final test.",
    startingPoint:
      "Start with one product family, one assembly area or one high-value project route.",
    finalCta:
      "Control your project-based manufacturing flow with real-time production visibility.",
    text: "Project MES for switchgear and electrical panel manufacturing.",
    features: [
      "Digital job orders by project",
      "Material kit control",
      "QR traceability for panels",
      "FAT and quality gates",
      "Photos and inspection records",
      "ERP confirmations",
    ],
  },
  {
    slug: "metal-processing-fabrication",
    title: "Metal Processing, Fabrication & Steel Service Centers",
    short:
      "Monitor cutting, bending, welding, machining, coating and finishing operations. Track WIP, setup, downtime, rework, quality and energy per job or ton.",
    seoTitle:
      "BEEDIGIT for Metal Processing & Fabrication | WIP, OEE, Rework & Energy per Ton",
    seoDescription:
      "BEEDIGIT helps metal fabrication and steel processing companies monitor cutting, bending, welding, machining, coating and finishing with real-time WIP, downtime, quality and energy data.",
    hero: {
      headline: "Control metal production from cutting to finished goods.",
      subheadline:
        "BEEDIGIT helps metal processing and fabrication companies reduce waiting, improve machine utilization, control WIP, track quality, manage maintenance and understand real production cost.",
      primaryCta: "Book Fabrication Visibility Assessment",
      secondaryCta: "Start with One Bottleneck Work Center",
    },
    problem: {
      intro:
        "Metal processing factories have many different work centers, each with different losses.",
      bullets: [
        "Cutting waits for material, nesting or program preparation.",
        "Bending waits for tools, setup or operator availability.",
        "Welding waits for parts, fixtures or quality decisions.",
        "Machining waits for programs, tools or measurements.",
        "Coating waits for batch size or surface preparation.",
        "Finishing and packing wait for inspection or transport.",
        "WIP is stuck between operations and actual routing time differs from costing standard.",
        "Rework is not clearly connected to root cause; energy cost is not linked to job, ton, part or operation.",
      ],
      closing:
        "ERP may know the order, but it rarely shows where the order is stuck, why it is delayed or which operation is the bottleneck.",
    },
    whyErpNotEnough:
      "Costing standards, routings and ERP order status cannot show which work center is actually starving, which operator is waiting or where energy is burned without output. Only real-time shop-floor data can.",
    howBeedigitHelps: [
      "Monitor machine status and output by work center.",
      "Track job order progress through cutting, bending, welding, machining, coating, finishing and packing.",
      "Use QR codes for material, parts, batches, pallets and finished goods.",
      "Capture actual setup time, processing time and waiting time.",
      "Show WIP status by order, location and operation.",
      "Track scrap and rework by reason.",
      "Digitize quality inspections.",
      "Use Andon for missing material, tool problems, quality holds and maintenance issues.",
      "Track energy consumption by machine, job, batch, kg or ton.",
      "Improve routing standards using actual production data.",
      "Support better scheduling with real capacity feedback.",
    ],
    modules: [
      "MES / Job Orders",
      "Machine Monitoring",
      "OEE & Downtime",
      "Material Flow",
      "QR Coding",
      "Quality Management",
      "CMMS / Maintenance",
      "Energy Management",
      "ANDON",
      "ERP / APS Integration",
    ],
    kpis: [
      "Machine utilization",
      "Job progress by operation",
      "WIP by work center",
      "Setup time",
      "Waiting time",
      "Cutting / bending / welding output",
      "Rework rate",
      "Scrap rate",
      "OEE by machine",
      "Downtime by reason",
      "Energy per job / kg / ton",
      "Plan vs actual routing time",
      "Bottleneck work center",
      "On-time order completion",
      "Maintenance downtime",
      "Quality hold time",
    ],
    dashboardWidgets: [
      "WIP by operation",
      "Cutting / bending / welding status",
      "Setup time",
      "Waiting time",
      "Rework",
      "Energy per job or ton",
    ],
    leanAngle:
      "BEEDIGIT supports WIP reduction, flow improvement, bottleneck management, setup time reduction, rework reduction, TPM, daily management boards and Pareto analysis of downtime and quality losses.",
    leanMessage: "Reduce waiting, WIP and rework across the metal production flow.",
    startingPoint:
      "Start with one bottleneck work center such as cutting, bending, welding, machining or coating.",
    finalCta:
      "Find your metal fabrication bottleneck and build real-time production visibility.",
    text: "Real-time WIP, quality and energy visibility for metal processing.",
    features: [
      "Work center monitoring",
      "WIP by operation",
      "Setup and waiting time",
      "QR traceability",
      "Energy per job or ton",
      "ERP / APS feedback",
    ],
  },
  {
    slug: "food-beverage-packaging",
    title: "Food, Beverage & Packaging Lines",
    short:
      "Improve line OEE, batch traceability, cleaning checks, packaging losses, micro-stops, quality discipline and energy per batch or product.",
    seoTitle:
      "BEEDIGIT for Food, Beverage & Packaging | OEE, Batch Traceability, Quality & Energy",
    seoDescription:
      "BEEDIGIT helps food, beverage and packaging manufacturers track OEE, micro-stops, batch traceability, cleaning checks, quality, packaging losses and energy per batch in real time.",
    hero: {
      headline: "Improve line performance, batch traceability and quality discipline.",
      subheadline:
        "Food, beverage and packaging factories need fast production, stable quality, reliable batch control and strong line performance. BEEDIGIT helps teams track OEE, downtime, cleaning, quality checks, batch traceability, packaging losses and energy per batch.",
      primaryCta: "Book Line Performance Assessment",
      secondaryCta: "Start with One Packaging Line",
    },
    problem: {
      intro: "Food and packaging lines lose performance in many small ways:",
      bullets: [
        "Short stops on packaging equipment and product changeovers.",
        "Cleaning and sanitation time.",
        "Material or packaging shortages and label issues.",
        "Quality holds, overweight / underweight issues, startup waste.",
        "Rejected batches, rework and delayed batch records.",
        "Line speed instability and energy use during idle time.",
      ],
      closing:
        "Manual reporting usually hides these losses. Traceability is also critical — factories need to connect raw material batches, production batches, packaging materials, quality checks and finished goods.",
    },
    whyErpNotEnough:
      "ERP holds recipes, batches and shipments — not the micro-stop on the filler or the reason a packer paused for 90 seconds. Without automatic line data, real OEE and batch discipline stay guesswork.",
    howBeedigitHelps: [
      "Monitor line status and OEE in real time.",
      "Track speed loss and micro-stops on production and packaging lines.",
      "Capture downtime reasons by line, product, batch and shift.",
      "Digitize quality checks, cleaning checks and production checklists.",
      "Track batch genealogy using QR codes.",
      "Connect packaging material usage to production orders.",
      "Track scrap, rework and giveaway where configured.",
      "Use Andon for material shortages, maintenance issues and quality holds.",
      "Track energy per batch, product, line or shift.",
      "Give supervisors live production status and shift performance.",
      "Support daily management meetings with real data.",
    ],
    modules: [
      "Machine Monitoring",
      "OEE & Downtime",
      "MES / Job Orders",
      "QR Traceability",
      "Quality Management",
      "Digital Checklists",
      "Material Flow",
      "ANDON",
      "Energy Management",
      "CMMS / Maintenance",
    ],
    kpis: [
      "OEE by line",
      "Line speed vs target",
      "Micro-stops",
      "Downtime by reason",
      "Changeover time",
      "Cleaning time",
      "Startup waste",
      "Scrap rate",
      "Rework rate",
      "First-pass quality",
      "Batch traceability completeness",
      "Packaging material loss",
      "Energy per batch / product",
      "Andon response time",
      "Maintenance downtime",
      "Output vs target",
      "Plan vs actual production time",
    ],
    dashboardWidgets: [
      "OEE by line",
      "Micro-stops",
      "Batch status",
      "Cleaning checks",
      "Packaging losses",
      "Energy per batch",
      "Quality holds",
    ],
    leanAngle:
      "BEEDIGIT supports short-stop reduction, changeover reduction, batch quality improvement, traceability discipline, standard work, daily SQCDP meetings, TPM and energy loss reduction.",
    leanMessage: "Make every stop, batch, check and loss visible.",
    startingPoint:
      "Start with one packaging line, filling line, production cell or high-loss product family.",
    finalCta: "Find the hidden losses inside your food or packaging line.",
    text: "Line OEE, batch traceability and quality discipline for food and packaging.",
    features: [
      "OEE and micro-stop tracking",
      "Digital cleaning and quality checks",
      "QR batch genealogy",
      "Packaging material loss",
      "Energy per batch",
      "Daily management dashboards",
    ],
  },
  {
    slug: "packaging-printing-converting",
    title: "Packaging, Printing & Converting",
    short:
      "Control roll-to-roll speed, setup, web breaks, roll changes, material waste, defects, roll traceability and energy consumption.",
    seoTitle:
      "BEEDIGIT for Packaging, Printing & Converting | Roll Speed, Waste, OEE & Traceability",
    seoDescription:
      "BEEDIGIT helps packaging, printing and converting factories control roll-to-roll speed, setup, web breaks, roll changes, defects, waste, roll traceability and energy consumption.",
    hero: {
      headline: "Control speed, waste and quality in roll-to-roll production.",
      subheadline:
        "In packaging, printing and converting, performance depends on stable speed, fast setups, material traceability and defect control. BEEDIGIT tracks line speed, output, downtime, waste, quality checks and energy in real time.",
      primaryCta: "Book Roll-to-Roll Visibility Assessment",
      secondaryCta: "Start with One Converting Line",
    },
    problem: {
      intro:
        "Roll-to-roll and converting operations are sensitive to speed, tension, setup and material quality.",
      bullets: [
        "Slow running and frequent short stops.",
        "Web breaks, roll change delays and setup / registration time.",
        "Printing defects, lamination defects and slitting waste.",
        "Material mismatch, quality holds and startup scrap.",
        "Energy loss during idle time.",
      ],
      closing:
        "Standard speeds are often theoretical. Slower means lost output; faster than the stable process window means defects and waste.",
    },
    whyErpNotEnough:
      "ERP knows the roll order and the customer specification, but the real speed loss, web break frequency and trim waste live only on the line. BEEDIGIT captures them automatically.",
    howBeedigitHelps: [
      "Track actual line speed and output by roll, order, product and shift.",
      "Compare actual speed vs standard speed.",
      "Track downtime and micro-stops.",
      "Capture web breaks, roll changes, setup, cleaning and quality holds.",
      "Use QR codes for rolls, WIP, pallets and finished goods.",
      "Connect quality checks to order, roll, machine and operator.",
      "Track scrap and trim waste.",
      "Monitor energy per roll, meter, kg or order.",
      "Analyze speed loss and defect Pareto.",
      "Improve planning standards with actual production data.",
      "Support daily improvement meetings with live dashboards.",
    ],
    modules: [
      "Machine Monitoring",
      "OEE & Downtime",
      "Line Speed Tracking",
      "QR Coding",
      "Quality Management",
      "Material Flow",
      "Energy Management",
      "MES / Job Orders",
      "ANDON",
      "CMMS / Maintenance",
    ],
    kpis: [
      "Actual speed vs target",
      "Output meters per shift",
      "OEE by line",
      "Web break frequency",
      "Roll change time",
      "Setup time",
      "Startup scrap",
      "Trim waste",
      "Defects by type",
      "First-pass yield",
      "Downtime by reason",
      "Micro-stops",
      "Energy per roll / meter",
      "Quality hold time",
      "Plan vs actual production time",
    ],
    dashboardWidgets: [
      "Roll speed",
      "Roll status",
      "Web breaks",
      "Setup time",
      "Trim waste",
      "Defects",
      "Energy per roll",
    ],
    leanAngle:
      "BEEDIGIT supports speed loss reduction, setup reduction, defect reduction, material waste reduction, quality root cause analysis, TPM, standard speed validation and continuous improvement dashboards.",
    leanMessage: "Reduce the hidden waste between speed, setup and quality.",
    startingPoint:
      "Start with one roll-to-roll line, printing machine, slitter, laminator or converting bottleneck.",
    finalCta: "See where your line loses speed, material and quality.",
    text: "Roll-to-roll speed, waste and traceability control.",
    features: [
      "Line speed vs standard",
      "Web break tracking",
      "Trim waste",
      "Roll QR traceability",
      "Energy per roll",
      "Defect Pareto",
    ],
  },
  {
    slug: "automotive-components-assembly",
    title: "Automotive Components & Repetitive Assembly",
    short:
      "Track takt time, station performance, Andon calls, quality gates, rework, serial traceability, material shortages and maintenance losses.",
    seoTitle:
      "BEEDIGIT for Automotive Components & Assembly | Takt, Andon, Quality & Traceability",
    seoDescription:
      "BEEDIGIT helps automotive component and assembly manufacturers track takt time, station performance, Andon calls, quality gates, serial traceability, rework and maintenance losses.",
    hero: {
      headline: "Run assembly lines with takt discipline and real-time visibility.",
      subheadline:
        "Automotive and component manufacturers need stable flow, traceability, quality discipline and fast reaction to line issues. BEEDIGIT helps teams monitor station performance, job progress, Andon calls, defects, rework and equipment availability.",
      primaryCta: "Book Assembly Line Assessment",
      secondaryCta: "Start with One Station Group",
    },
    problem: {
      intro:
        "Assembly operations lose performance when takt time, material flow, quality and maintenance are not synchronized.",
      bullets: [
        "Station cycle time variation and bottleneck stations.",
        "Missing parts and quality issues found too late.",
        "Manual rework tracking and unclear Andon response time.",
        "Paper checklists and incomplete serial traceability.",
        "Delayed production confirmations and unplanned equipment downtime.",
        "Line stops caused by small unresolved issues.",
        "Real station losses hidden behind total line output.",
      ],
      closing:
        "If the factory cannot see which station is losing time, continuous improvement becomes based on opinion instead of data.",
    },
    whyErpNotEnough:
      "ERP records the finished vehicle or component, not the 12 seconds a station lost per cycle. BEEDIGIT connects takt, Andon and quality at source to real orders and serials.",
    howBeedigitHelps: [
      "Track production output and cycle time by station or line.",
      "Compare actual cycle time vs takt time.",
      "Identify bottleneck stations.",
      "Use digital work instructions and standard work checklists.",
      "Track Andon calls and response time.",
      "Capture defects, rework and quality checks by product, station, operator and batch.",
      "Use QR codes or serial tracking for parts, assemblies and finished products.",
      "Monitor equipment downtime and maintenance events.",
      "Track material shortages and escalation reasons.",
      "Support SQCDP boards and daily management meetings.",
      "Feed production confirmations and quality results to ERP.",
    ],
    modules: [
      "MES / Job Orders",
      "Machine Monitoring",
      "OEE & Downtime",
      "ANDON",
      "Quality Management",
      "QR / Serial Traceability",
      "Material Flow",
      "CMMS / Maintenance",
      "Dashboards",
      "ERP Integration",
    ],
    kpis: [
      "Takt time vs cycle time",
      "Station cycle time",
      "Bottleneck station",
      "Output vs target",
      "Line OEE",
      "Andon calls by category",
      "Andon response time",
      "First-pass yield",
      "Defects by station",
      "Rework rate",
      "Material shortage events",
      "Downtime by reason",
      "Maintenance response time",
      "Plan vs actual output",
      "Shift performance",
      "Action closure rate",
    ],
    dashboardWidgets: [
      "Takt vs cycle time",
      "Station performance",
      "Andon calls",
      "First-pass yield",
      "Rework",
      "Material shortages",
    ],
    leanAngle:
      "BEEDIGIT supports takt discipline, standard work, Jidoka, Andon escalation, quality at source, station bottleneck analysis, rework reduction, daily SQCDP management and Six Sigma defect analysis.",
    leanMessage: "Control takt, quality and flow with real-time shop-floor data.",
    startingPoint:
      "Start with one assembly line, station group, bottleneck station or quality-critical process.",
    finalCta: "Build digital assembly visibility from station to management dashboard.",
    text: "Takt, Andon and quality at source for repetitive assembly.",
    features: [
      "Takt vs cycle tracking",
      "Andon workflows",
      "Serial traceability",
      "Quality at source",
      "Station bottleneck analysis",
      "SQCDP dashboards",
    ],
  },
  {
    slug: "wood-furniture-fitout",
    title: "Wood, Furniture & Fit-Out Manufacturing",
    short:
      "Manage custom orders, cutting, CNC, sanding, painting, assembly, quality, WIP, material usage and installation readiness.",
    seoTitle:
      "BEEDIGIT for Wood, Furniture & Fit-Out | Job Orders, WIP, CNC, Quality & Material Flow",
    seoDescription:
      "BEEDIGIT helps wood, furniture and fit-out manufacturers control custom orders, cutting, CNC, sanding, painting, assembly, quality, WIP, material usage and delivery readiness.",
    hero: {
      headline: "Make custom production visible from material cutting to final installation.",
      subheadline:
        "Wood, furniture and fit-out production combines project-based orders, material-sensitive processes, CNC machines, manual work, finishing, assembly and quality checks. BEEDIGIT helps manufacturers control job progress, WIP, rework, material flow and production performance.",
      primaryCta: "Book Custom Production Assessment",
      secondaryCta: "Start with One Production Area",
    },
    problem: {
      intro:
        "Wood and fit-out manufacturers often struggle with visibility because production is a mix of machine operations and manual work.",
      bullets: [
        "Custom order complexity and missing materials, accessories or hardware.",
        "Unclear job status and manual production updates.",
        "Rework caused by wrong dimensions or surface defects.",
        "WIP waiting between cutting, CNC, sanding, painting and assembly.",
        "Bottlenecks in finishing or assembly; unclear labor time per order.",
        "Delayed installation readiness and late-detected quality issues.",
        "ERP shows the order but not the real part / order location.",
      ],
      closing:
        "Custom production breaks down when part location, WIP and rework are only known to the person who last touched the job.",
    },
    whyErpNotEnough:
      "ERP tracks orders, not panels. It cannot tell you whether the parts for tomorrow's installation are already sanded, painted or waiting for a missing hinge. BEEDIGIT does.",
    howBeedigitHelps: [
      "Create digital job orders for cutting, CNC, sanding, painting, assembly and packing.",
      "Track order progress by operation and work center.",
      "Use QR codes for parts, panels, kits, pallets and finished products.",
      "Give operators digital drawings, specifications and checklists.",
      "Track output, scrap, rework and quality issues.",
      "Monitor CNC and key machines where connected.",
      "Track WIP location and job status.",
      "Capture reasons for waiting, rework and quality hold.",
      "Use Andon for missing materials, drawing issues, quality problems or maintenance.",
      "Track actual labor and production time vs planned time.",
      "Give management live visibility of project and order readiness.",
    ],
    modules: [
      "MES / Job Orders",
      "Material Flow",
      "QR Coding",
      "Quality Management",
      "Machine Monitoring",
      "ANDON",
      "Dashboards",
      "CMMS / Maintenance",
      "Energy Management",
    ],
    kpis: [
      "Job progress by order",
      "WIP by operation",
      "Cutting output",
      "CNC utilization",
      "Assembly progress",
      "Rework rate",
      "Scrap material",
      "Quality defects",
      "Missing material delays",
      "Waiting time",
      "Plan vs actual labor time",
      "Bottleneck operation",
      "On-time order readiness",
      "Installation readiness status",
      "Production lead time",
      "Operator confirmations",
    ],
    dashboardWidgets: [
      "Order progress",
      "WIP by operation",
      "CNC utilization",
      "Missing materials",
      "Rework",
      "Installation readiness",
    ],
    leanAngle:
      "BEEDIGIT supports WIP reduction, rework reduction, visual order tracking, standard work, bottleneck management, material flow control, daily production meetings and quality root cause analysis.",
    leanMessage: "Make custom manufacturing flow visible and controllable.",
    startingPoint:
      "Start with job orders, QR tracking and WIP visibility in one production area.",
    finalCta: "Digitize your custom production flow from material to finished order.",
    text: "Custom production visibility from material to installation.",
    features: [
      "Digital job orders",
      "QR part tracking",
      "CNC utilization",
      "Rework and quality tracking",
      "Installation readiness",
      "Andon workflows",
    ],
  },
  {
    slug: "energy-intensive-manufacturing",
    title: "Energy-Intensive Manufacturing",
    short:
      "Measure energy by machine, line, order, batch, product, meter, kilogram or ton. Identify idle energy, inefficient processes and ISO 50001 improvement opportunities.",
    seoTitle:
      "BEEDIGIT for Energy-Intensive Manufacturing | Energy per Unit, OEE & ISO 50001 Support",
    seoDescription:
      "BEEDIGIT helps energy-intensive manufacturers track energy by machine, line, order, batch, product, meter, kilogram or ton and connect energy losses with production context.",
    hero: {
      headline: "Understand the real energy cost of production.",
      subheadline:
        "Monthly energy bills do not show where energy is lost. BEEDIGIT connects energy consumption with machines, orders, products, batches, downtime, idle time and output so manufacturers can manage energy as a production loss.",
      primaryCta: "Book Energy Visibility Assessment",
      secondaryCta: "Start with One Energy-Intensive Line",
    },
    problem: {
      intro:
        "Energy-intensive factories often know total kWh consumption, but not the real energy cost of each product, machine, line, order or shift.",
      bullets: [
        "High energy use during idle machine time.",
        "Machines consuming energy while waiting for material or operators.",
        "Inefficient startup and shutdown behavior.",
        "Energy peaks without production context.",
        "Energy standards not connected to actual production.",
        "Wrong energy cost allocation in product costing.",
        "Poor visibility of energy per unit, kg, meter, batch or ton.",
        "ISO 50001 initiatives based on delayed or incomplete data.",
      ],
      closing:
        "Without production context, energy data is just a utility number. BEEDIGIT turns it into operational improvement data.",
    },
    whyErpNotEnough:
      "ERP allocates energy cost by month or by center, not by product, order or idle event. Utility meters see totals; BEEDIGIT sees the operational context behind every kWh.",
    howBeedigitHelps: [
      "Track energy by machine, line, order, batch, shift, product, meter, kg or ton.",
      "Connect energy consumption with machine state: running, stopped, idle, setup or maintenance.",
      "Identify idle energy loss.",
      "Compare energy per unit against production standards.",
      "Detect abnormal energy consumption.",
      "Support ISO 50001 energy management processes with production-based energy KPIs.",
      "Show energy cost linked to actual output.",
      "Help Lean teams treat energy waste as operational waste.",
      "Feed energy KPIs to dashboards, ERP, BI or management reports.",
    ],
    modules: [
      "Energy Management",
      "Machine Monitoring",
      "OEE & Downtime",
      "MES / Job Orders",
      "Dashboards",
      "CMMS / Maintenance",
      "ERP / BI Integration",
    ],
    kpis: [
      "Energy per unit",
      "Energy per meter / kg / ton",
      "Energy per batch / order",
      "Energy per machine / line",
      "Idle energy loss",
      "Energy during downtime",
      "Peak load events",
      "Energy baseline",
      "Actual energy vs standard energy",
      "Energy cost per product",
      "Energy waste by downtime reason",
      "Energy performance improvement",
    ],
    dashboardWidgets: [
      "Energy per unit",
      "Idle energy",
      "Peak load",
      "Energy by machine",
      "Energy during downtime",
      "Actual vs standard energy",
    ],
    leanAngle:
      "BEEDIGIT helps operational excellence teams include energy waste in Kaizen, TPM and Six Sigma projects. Teams can analyze idle energy, abnormal consumption, equipment condition, process instability and energy per useful output.",
    leanMessage: "Make energy waste visible as a production loss.",
    startingPoint:
      "Start with the most energy-intensive machines, lines or process areas.",
    finalCta: "Build your energy visibility baseline with real production context.",
    text: "Energy per unit and idle energy visibility for energy-intensive plants.",
    features: [
      "Energy per unit / kg / ton",
      "Idle energy detection",
      "Machine-state energy context",
      "ISO 50001 support",
      "Actual vs standard energy",
      "BI / ERP feedback",
    ],
  },
  {
    slug: "manufacturing-holdings",
    title: "Multi-Site Manufacturing Holdings",
    short:
      "Standardize MES, OEE, Lean KPIs, energy, maintenance, quality and production execution across factories while integrating with ERP, APS and BI.",
    seoTitle:
      "BEEDIGIT for Manufacturing Holdings | Multi-Site MES, OEE, Lean KPIs & Energy",
    seoDescription:
      "BEEDIGIT helps manufacturing groups standardize MES, OEE, Lean KPIs, energy, maintenance, quality and production execution across factories while integrating with ERP, APS and BI.",
    hero: {
      headline: "One shop-floor data standard for every factory in the group.",
      subheadline:
        "BEEDIGIT helps manufacturing groups create a common digital production layer across plants, industries and business units. Standardize OEE, downtime, job execution, QR traceability, energy, maintenance, quality and Lean dashboards while keeping ERP, APS and BI integrated.",
      primaryCta: "Book Multi-Site Standardization Assessment",
      secondaryCta: "Start with One Pilot Plant",
    },
    problem: {
      intro:
        "Manufacturing groups often have different factories using different systems, different KPI definitions and different reporting logic.",
      bullets: [
        "Every factory calculates OEE differently.",
        "Downtime reasons are not standardized.",
        "Production reports are delayed and ERP data does not reflect real shop-floor status.",
        "Energy performance is not comparable between plants.",
        "Quality and maintenance data are separated.",
        "Local Excel files become the real operating system.",
        "Group management cannot compare factories fairly.",
        "Best practices are difficult to scale; enterprise MES projects can become heavy and slow.",
      ],
      closing:
        "Manufacturing holdings need a practical standard that can start plant by plant and scale across the group.",
    },
    whyErpNotEnough:
      "Group ERP consolidates finance and inventory, not real production performance. Without a common shop-floor data layer, benchmarking between plants stays political instead of factual.",
    howBeedigitHelps: [
      "Create a standardized shop-floor data model across factories.",
      "Start with pilot lines and scale to multiple plants.",
      "Standardize OEE, downtime reason trees, job order statuses and Lean KPIs.",
      "Use industry-specific templates for different production types.",
      "Connect ERP, APS, BI and local machine data.",
      "Give plant managers real-time factory dashboards.",
      "Give group management multi-site performance visibility.",
      "Track energy per product, line, factory or business unit.",
      "Standardize QR traceability and quality workflows.",
      "Support centralized continuous improvement programs.",
      "Compare factories based on trusted real production data.",
    ],
    modules: [
      "Enterprise MES",
      "Machine Monitoring",
      "OEE & Downtime",
      "Multi-Site Dashboards",
      "Material Flow",
      "QR Traceability",
      "Quality Management",
      "CMMS / Maintenance",
      "Energy Management",
      "ANDON",
      "ERP / APS / BI Integration",
      "Role-Based Access",
    ],
    kpis: [
      "OEE by factory / line",
      "Standardized downtime Pareto",
      "Output vs target",
      "Energy per unit / factory",
      "Maintenance downtime",
      "MTBF",
      "MTTR",
      "Quality rate",
      "Scrap rate",
      "Production plan adherence",
      "ERP confirmation accuracy",
      "Andon response time",
      "Action closure rate",
      "Best-practice replication",
      "Multi-site benchmark performance",
    ],
    dashboardWidgets: [
      "OEE by factory",
      "Energy by factory",
      "Downtime Pareto by plant",
      "Quality rate",
      "Action closure",
      "Benchmark comparison",
    ],
    leanAngle:
      "BEEDIGIT helps manufacturing groups scale operational excellence through common OEE standards, common loss reason classification, SQCDP dashboards, group-wide Kaizen tracking, TPM standardization, Six Sigma project baselines, energy benchmarking and daily management across plants.",
    leanMessage: "Standardize continuous improvement across every factory.",
    startingPoint:
      "Start with one pilot plant, one production line or one common KPI standard across selected factories.",
    finalCta: "Build your multi-site factory visibility standard.",
    text: "One shop-floor data standard across the group.",
    features: [
      "Standardized OEE and loss reasons",
      "Multi-site dashboards",
      "Energy benchmarking",
      "Group-wide Kaizen tracking",
      "Common QR and quality workflows",
      "ERP / APS / BI integration",
    ],
  },
  {
    slug: "small-mid-size-manufacturers",
    title: "Small & Mid-Size Manufacturers",
    short:
      "Start with one machine, one line or one bottleneck using Plug & Play OEE, job orders, QR tracking, quality checks and dashboards.",
    seoTitle:
      "BEEDIGIT for Small Manufacturers | Plug & Play MES, OEE, Job Orders & QR",
    seoDescription:
      "BEEDIGIT helps small and mid-size manufacturers start with one machine, one line or one bottleneck using Plug & Play OEE, job orders, QR tracking, quality checks and dashboards.",
    hero: {
      headline: "MES and OEE for small factories without enterprise complexity.",
      subheadline:
        "BEEDIGIT makes professional factory digitalization affordable and practical for small and mid-size manufacturers. Start with the most important machines, remove manual reporting and see where output, time and money are lost.",
      primaryCta: "Book Practical Digitalization Assessment",
      secondaryCta: "Start with One Machine",
    },
    problem: {
      intro:
        "Small factories often know they need better control, but traditional MES projects feel too expensive, too long and too complex.",
      bullets: [
        "Production status is managed by phone calls and paper.",
        "Machine downtime is not measured; output is recorded manually.",
        "Job priorities are unclear and customer delivery dates are difficult to promise.",
        "Quality issues are tracked in notebooks or Excel.",
        "Material movement is not visible; the owner does not know the real profit per job.",
        "Improvement is based on experience, not data.",
        "ERP, if available, does not show the real shop-floor situation.",
      ],
      closing:
        "Small manufacturers do not need a huge transformation project. They need a practical starting point.",
    },
    whyErpNotEnough:
      "Small factory ERP or accounting software captures invoices, not machine downtime. Excel logs are always late. BEEDIGIT gives owners and supervisors real-time production truth with minimal IT.",
    howBeedigitHelps: [
      "Start with one bottleneck, one line or one critical machine.",
      "Track machine running, stopping, output and downtime.",
      "Use simple digital job orders instead of paper.",
      "Give operators BEEPAD tablets for confirmations and quality checks.",
      "Print QR labels for materials, WIP and finished goods.",
      "Track production progress in real time.",
      "See OEE and downtime dashboards.",
      "Identify the biggest losses before investing in new machines.",
      "Use SaaS and preconfigured hardware without building a large IT team.",
      "Expand step by step as value is proven.",
    ],
    modules: [
      "Machine Monitoring",
      "OEE & Downtime",
      "Job Orders",
      "QR Coding",
      "Basic Quality",
      "Dashboards",
      "ANDON",
      "CMMS light",
      "Energy Tracking (where needed)",
    ],
    kpis: [
      "Machine running time",
      "Downtime by reason",
      "Output vs target",
      "OEE",
      "Job completion status",
      "Manual reporting reduction",
      "Scrap",
      "Rework",
      "Delivery delay reasons",
      "Operator confirmations",
      "Machine utilization",
      "Energy per product (where configured)",
    ],
    dashboardWidgets: [
      "Running / stopped",
      "Daily output",
      "Simple OEE",
      "Job status",
      "Downtime reasons",
      "Operator confirmations",
    ],
    leanAngle:
      "BEEDIGIT helps small factories introduce Lean in a practical way: see losses, reduce waiting, reduce downtime, improve flow, standardize work, measure improvement and run daily production meetings with facts.",
    leanMessage: "You do not need a big MES project to start continuous improvement.",
    startingPoint: "Start with one machine, one line or one bottleneck.",
    finalCta: "Get your first real-time production visibility project.",
    text: "Plug & Play MES and OEE for small and mid-size manufacturers.",
    features: [
      "Start with one machine",
      "Simple digital job orders",
      "OEE and downtime dashboards",
      "QR labels for materials and WIP",
      "Operator tablets",
      "Preconfigured hardware",
    ],
  },
];

export const industryBySlug = (slug: string) =>
  industries.find((i) => i.slug === slug);
